/**
 * OneDrive Auth Page
 *
 * This page is served on the central auth domain (e.g., mylogin.dev.kenbakke.com)
 * and handles OneDrive/Microsoft OAuth authentication for multi-tenant environments.
 *
 * Flow:
 * 1. Tenant subdomain opens this page in a popup with returnOrigin and optional expectedEmail
 * 2. User clicks to authenticate with Microsoft
 * 3. If expectedEmail provided, validates the authenticated user's email matches
 * 4. Sends the result back to the tenant via postMessage
 * 5. Closes the popup
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import { Button, CircularProgress, Typography, Box, Alert } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import { msalConfig } from "../../../common/utilities/constants/constants";
import {
    isValidOrigin,
    getMainDomain,
    sendMessageToOpener,
    GooglePickerMessage
} from "../../../common/utilities/google-picker/google-picker-message.util";

import "./onedrive-auth.scss";

interface OneDriveAuthResult {
    success: boolean;
    accessToken?: string;
    email?: string;
    error?: string;
}

type AuthStatus =
    | "initializing"
    | "ready"
    | "authenticating"
    | "success"
    | "error"
    | "invalid-origin";

const OneDriveAuthPage = () => {
    console.log("OneDriveAuthPage rendering, pathname:", window.location.pathname);

    const [status, setStatus] = useState<AuthStatus>("initializing");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [msalInstance] = useState(() => new PublicClientApplication(msalConfig));
    const [msalInitialized, setMsalInitialized] = useState(false);

    // Get parameters from URL
    const { returnOrigin, expectedEmail } = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return {
            returnOrigin: params.get("returnOrigin") || "",
            expectedEmail: params.get("expectedEmail") || ""
        };
    }, []);

    // Validate the return origin
    const isOriginValid = useMemo(() => {
        if (!returnOrigin) return false;
        const mainDomain = getMainDomain();
        return isValidOrigin(returnOrigin, [mainDomain]);
    }, [returnOrigin]);

    // Initialize MSAL
    useEffect(() => {
        const initMsal = async () => {
            try {
                await msalInstance.initialize();
                await msalInstance.handleRedirectPromise();
                setMsalInitialized(true);
                console.log("OneDriveAuthPage: MSAL initialized");
            } catch (error) {
                console.error("OneDriveAuthPage: MSAL initialization error", error);
                setErrorMessage("Failed to initialize Microsoft authentication.");
                setStatus("error");
            }
        };

        if (isOriginValid) {
            initMsal();
        }
    }, [msalInstance, isOriginValid]);

    // Send result to parent and close
    const sendResultAndClose = useCallback(
        (result: OneDriveAuthResult) => {
            console.log("OneDriveAuthPage: Sending result to parent", result);

            const message: GooglePickerMessage = {
                type: "GOOGLE_PICKER_RESULT", // Reuse same message type for compatibility
                payload: {
                    success: result.success,
                    accessToken: result.accessToken,
                    url: result.email, // Repurpose url field for email
                    error: result.error
                },
                origin: window.location.origin
            };

            sendMessageToOpener(message, returnOrigin);

            setTimeout(() => {
                window.close();
            }, 100);
        },
        [returnOrigin]
    );

    // Send cancel to parent
    const sendCancelAndClose = useCallback(() => {
        console.log("OneDriveAuthPage: User cancelled");

        const message: GooglePickerMessage = {
            type: "GOOGLE_PICKER_CANCEL",
            origin: window.location.origin
        };

        sendMessageToOpener(message, returnOrigin);

        setTimeout(() => {
            window.close();
        }, 100);
    }, [returnOrigin]);

    // Initialize and validate on mount
    useEffect(() => {
        if (!returnOrigin) {
            setStatus("invalid-origin");
            setErrorMessage("Missing return origin parameter");
            return;
        }

        if (!isOriginValid) {
            setStatus("invalid-origin");
            setErrorMessage(
                "Invalid origin. This page can only be opened from authorized domains."
            );
            return;
        }

        if (!msalConfig.auth.clientId) {
            setStatus("error");
            setErrorMessage("Microsoft authentication is not configured.");
            return;
        }

        // Status will be set to ready once MSAL is initialized
    }, [returnOrigin, isOriginValid]);

    // Set ready once MSAL is initialized
    useEffect(() => {
        if (msalInitialized && isOriginValid && status === "initializing") {
            setStatus("ready");
        }
    }, [msalInitialized, isOriginValid, status]);

    // Handle authenticate button click
    const handleAuthenticate = useCallback(async () => {
        if (!msalInitialized) {
            setErrorMessage("Microsoft authentication is not ready. Please wait.");
            return;
        }

        setStatus("authenticating");

        try {
            const scopesConfig = {
                scopes: ["Files.Read.All", "offline_access"]
            };

            console.log("OneDriveAuthPage: Starting MSAL login popup");
            const loginResponse = await msalInstance.loginPopup(scopesConfig);

            console.log("OneDriveAuthPage: Login successful, acquiring token");
            const tokenResponse = await msalInstance.acquireTokenSilent({
                scopes: scopesConfig.scopes,
                account: loginResponse.account
            });

            const userEmail = loginResponse.account.username;
            console.log("OneDriveAuthPage: User email:", userEmail);

            // Validate email if expectedEmail was provided
            if (expectedEmail && userEmail !== expectedEmail) {
                console.log("OneDriveAuthPage: Email mismatch", {
                    expected: expectedEmail,
                    got: userEmail
                });
                setErrorMessage(`Please sign in with the account: ${expectedEmail}`);
                setStatus("error");
                return;
            }

            // Success - send result
            setStatus("success");
            sendResultAndClose({
                success: true,
                accessToken: tokenResponse.accessToken,
                email: userEmail
            });
        } catch (error: any) {
            console.error("OneDriveAuthPage: Authentication error", error);

            // Handle user cancellation
            if (error.errorCode === "user_cancelled" || error.errorMessage?.includes("cancelled")) {
                setStatus("ready");
                return;
            }

            setErrorMessage("Microsoft authentication failed. Please try again.");
            setStatus("error");
        }
    }, [msalInstance, msalInitialized, expectedEmail, sendResultAndClose]);

    // Handle cancel
    const handleCancel = useCallback(() => {
        sendCancelAndClose();
    }, [sendCancelAndClose]);

    // Handle retry
    const handleRetry = useCallback(() => {
        setErrorMessage("");
        setStatus("ready");
    }, []);

    // Render based on status
    const renderContent = () => {
        switch (status) {
            case "initializing":
                return (
                    <Box className="onedrive-auth-page__loading">
                        <CircularProgress size={40} />
                        <Typography variant="body1">Initializing...</Typography>
                    </Box>
                );

            case "invalid-origin":
                return (
                    <Box className="onedrive-auth-page__error">
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errorMessage}
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                            Please close this window and try again from the application.
                        </Typography>
                    </Box>
                );

            case "error":
                return (
                    <Box className="onedrive-auth-page__error">
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errorMessage}
                        </Alert>
                        <Box className="onedrive-auth-page__actions">
                            <Button variant="contained" onClick={handleRetry}>
                                Try Again
                            </Button>
                            <Button variant="text" onClick={handleCancel} sx={{ mt: 1 }}>
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                );

            case "ready":
                return (
                    <Box className="onedrive-auth-page__ready">
                        <AccountCircleIcon sx={{ fontSize: 64, color: "#0078d4", mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            OneDrive Authentication
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Click the button below to sign in with Microsoft.
                        </Typography>
                        {expectedEmail && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Please use the account: <strong>{expectedEmail}</strong>
                            </Typography>
                        )}
                        <Box className="onedrive-auth-page__actions">
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleAuthenticate}
                                sx={{
                                    backgroundColor: "#0078d4",
                                    "&:hover": { backgroundColor: "#106ebe" }
                                }}
                                startIcon={
                                    <img
                                        src="https://docs.microsoft.com/en-us/azure/active-directory/develop/media/howto-add-branding-in-azure-ad-apps/ms-symbollockup_mssymbol_19.svg"
                                        alt="Microsoft"
                                        style={{ width: 20, height: 20 }}
                                        onError={e => {
                                            // Fallback if image fails to load
                                            (e.target as HTMLImageElement).style.display = "none";
                                        }}
                                    />
                                }
                            >
                                Sign in with Microsoft
                            </Button>
                            <Button variant="text" onClick={handleCancel} sx={{ mt: 1 }}>
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                );

            case "authenticating":
                return (
                    <Box className="onedrive-auth-page__loading">
                        <CircularProgress size={40} />
                        <Typography variant="body1">Authenticating...</Typography>
                        <Button variant="text" onClick={handleCancel} sx={{ mt: 2 }}>
                            Cancel
                        </Button>
                    </Box>
                );

            case "success":
                return (
                    <Box className="onedrive-auth-page__loading">
                        <CircularProgress size={40} />
                        <Typography variant="body1">Authentication complete. Closing...</Typography>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <div className="onedrive-auth-page">
            <div className="onedrive-auth-page__container">{renderContent()}</div>
        </div>
    );
};

export default OneDriveAuthPage;
